/*
  The body of the calendar entries modal (see CalendarEntriesModal).

  Opens once the credential itself is saved in EMMS, so there is a
  calendarCredentialId to hang meeting spaces off. Its only job is to turn the
  entries the sign-in returned into meeting spaces, one of two ways:

    Import - the rooms are not in EMMS yet. Each entry gets a location (and a
             capacity, because the column is NOT NULL) and becomes a meeting
             space carrying the calendar entry's own name.
    Map    - the rooms are already in EMMS. Entries are matched to meeting
             spaces by name and the operator confirms the pairs before the
             calendar ids are written onto those rows.
    Later  - close; the credential is saved either way and this can be run
             again by re-authenticating from Edit Calendar.

  The calendar id gets its own column rather than hiding behind a friendly
  label: it is what lands on the MeetingSpace row as calendarMeetingId, and it
  is the one value an operator has to reconcile against a provider console.
  Click it to copy. Location and meeting room are picked by name, so their ids
  stay out of the table - they are chosen from the dropdowns, not typed.

  Both write paths go through one EMMS call, POST /meetingspace/bulkcreate,
  which answers with a verdict per entry rather than failing the batch - so the
  summary below can say exactly which rows were skipped and why.

  sx / inline styles only, per the note in CalendarOAuthPanel about the two
  makeStyles versions in this app sharing a namespace.
*/

import React, { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import LinkIcon from "@mui/icons-material/Link";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import ScheduleIcon from "@mui/icons-material/Schedule";

import {
  PROVISION_MODES,
  bulkCreateMeetingSpaces,
  fetchEmmsList,
  locationLabel,
  matchEntriesToMeetingSpaces,
} from "../utils/calendarProvisioning";

const DEFAULT_CAPACITY = 1;

const ChoiceCard = ({ icon, title, caption, onClick, disabled }) => (
  <Box
    role="button"
    tabIndex={disabled ? -1 : 0}
    onClick={disabled ? undefined : onClick}
    onKeyDown={(event) => {
      if (disabled) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    }}
    sx={{
      flex: 1,
      minWidth: 210,
      display: "flex",
      flexDirection: "column",
      gap: 0.75,
      p: 2,
      borderRadius: 2,
      border: "2px solid",
      borderColor: "divider",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
      transition: "border-color .15s, box-shadow .15s, transform .15s",
      "&:hover": disabled
        ? {}
        : {
            borderColor: "primary.main",
            boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
            transform: "translateY(-1px)",
          },
      "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
    }}
  >
    <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
      {title}
    </Typography>
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {caption}
    </Typography>
  </Box>
);

/*
  An id, shown in full on hover and copied on click. Clipboard access is blocked
  outside a secure context, which is why the value is on screen either way.
*/
const IdCell = ({ value, width = 200 }) => {
  const [copied, setCopied] = useState(false);

  if (!value) {
    return (
      <Typography variant="caption" sx={{ color: "text.disabled" }}>
        —
      </Typography>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      /* nothing to do - the id is readable on screen */
    }
  };

  return (
    <Tooltip title={copied ? "Copied" : value}>
      <Box
        component="span"
        onClick={copy}
        sx={{
          display: "block",
          maxWidth: width,
          fontFamily: "monospace",
          fontSize: "0.72rem",
          color: "text.secondary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "pointer",
          "&:hover": { color: "primary.main" },
        }}
      >
        {value}
      </Box>
    </Tooltip>
  );
};

const statusColour = (status) =>
  ({
    created: "success",
    updated: "success",
    skipped: "warning",
    failed: "error",
  }[status] || "default");

const CalendarProvisioningStep = ({
  calendarCredentialId,
  credentialName,
  calendars,
  emmsId,
  emmsHeaders,
  requestConfig,
  onSaveCredential,
  onDone,
}) => {
  const entries = useMemo(
    () => (Array.isArray(calendars) ? calendars : []),
    [calendars]
  );

  const [mode, setMode] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [summary, setSummary] = useState(null);

  /*
    This modal opens as soon as the sign-in returns entries, which is usually
    before the credential itself has been written to EMMS - there is nothing to
    hang meeting rooms off yet. Rather than making the operator go back and save
    first, the save is folded into the one button they were going to press
    anyway; onSaveCredential does it and hands back the stored row.
  */
  const [credentialId, setCredentialId] = useState(calendarCredentialId || "");
  const [savingCredential, setSavingCredential] = useState(false);

  const ensureCredential = async () => {
    if (credentialId) return credentialId;
    if (typeof onSaveCredential !== "function") {
      throw new Error("The calendar credential has not been saved yet.");
    }
    setSavingCredential(true);
    try {
      const saved = await onSaveCredential();
      const id = saved?.id;
      if (!id) {
        throw new Error(
          "EMMS saved the credential but did not return its id, so meeting rooms cannot be linked to it."
        );
      }
      setCredentialId(id);
      return id;
    } finally {
      setSavingCredential(false);
    }
  };

  const [locations, setLocations] = useState([]);
  const [meetingSpaces, setMeetingSpaces] = useState([]);

  const [importRows, setImportRows] = useState(() =>
    Object.fromEntries(
      (Array.isArray(calendars) ? calendars : []).map((entry) => [
        entry.id,
        { selected: true, locationId: "", capacity: "" },
      ])
    )
  );
  const [mapRows, setMapRows] = useState({});
  const [bulkLocationId, setBulkLocationId] = useState("");
  const [defaultCapacity, setDefaultCapacity] = useState(DEFAULT_CAPACITY);

 
  const loadFor = async (nextMode) => {
    setLoading(true);
    setLoadError("");
    try {
      if (nextMode === PROVISION_MODES.IMPORT) {
        const data = await fetchEmmsList({
          path: "location",
          emmsId,
          emmsHeaders,
          requestConfig,
        });
        setLocations(data);
        if (data.length === 0) {
          setLoadError(
            "No locations found. Add a location first."
          );
        }
      } else {
        const data = await fetchEmmsList({
          path: "meetingspace",
          emmsId,
          emmsHeaders,
          requestConfig,
        });
        setMeetingSpaces(data);
        const matches = matchEntriesToMeetingSpaces(entries, data);
        setMapRows(
          Object.fromEntries(
            matches.map(({ calendar, meetingSpace }) => [
              calendar.id,
              {
                // Only pre-tick the rows we actually matched; an operator
                // ticking the rest is a deliberate act, not an oversight.
                selected: Boolean(meetingSpace),
                meetingSpaceId: meetingSpace?.id || "",
              },
            ])
          )
        );
        if (data.length === 0) {
          setLoadError(
            "No meeting spaces found. Use Import instead."
          );
        }
      }
    } catch (e) {
      setLoadError(
        e?.response?.data || e?.message || "Could not load data from EMMS."
      );
    } finally {
      setLoading(false);
    }
  };

  const chooseMode = (nextMode) => {
    setMode(nextMode);
    setSummary(null);
    loadFor(nextMode);
  };

  /*
    "Do it later" still saves the credential, and with it the account's
    calendars - the forms put them under customProperties.calendarEntries, which
    is what turns Calendar Meeting ID on a meeting space into a dropdown. Going
    away empty-handed would mean typing those ids in by hand later, which is the
    thing this whole flow exists to avoid.
  */
  const doItLater = async () => {
    setSubmitting(true);
    setLoadError("");
    try {
      await ensureCredential();
      onDone();
    } catch (e) {
      setLoadError(
        e?.response?.data ||
          e?.message ||
          "The calendar credential could not be saved."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const backToChoices = () => {
    setMode("");
    setSummary(null);
    setLoadError("");
  };

  const applyLocationToAll = (locationId) => {
    setBulkLocationId(locationId);
    setImportRows((previous) =>
      Object.fromEntries(
        Object.entries(previous).map(([id, row]) => [id, { ...row, locationId }])
      )
    );
  };

  const selectedImportRows = entries.filter(
    (entry) => importRows[entry.id]?.selected
  );
  const importReady =
    selectedImportRows.length > 0 &&
    selectedImportRows.every((entry) => importRows[entry.id]?.locationId);

  const selectedMapRows = entries.filter((entry) => mapRows[entry.id]?.selected);
  const mapReady =
    selectedMapRows.length > 0 &&
    selectedMapRows.every((entry) => mapRows[entry.id]?.meetingSpaceId);

  // A meeting space can be mapped to one calendar entry only.
  const claimedBy = new Map(
    Object.entries(mapRows)
      .filter(([, row]) => row?.meetingSpaceId)
      .map(([entryId, row]) => [row.meetingSpaceId, entryId])
  );
  const mapOptionsFor = (entryId) =>
    meetingSpaces.filter((meetingSpace) => {
      const claimant = claimedBy.get(meetingSpace.id);
      if (claimant) return claimant === entryId;
      return (
        !meetingSpace.calendarMeetingId ||
        meetingSpace.calendarMeetingId === entryId
      );
    });

  const matchedCount = entries.filter((entry) =>
    Boolean(mapRows[entry.id]?.meetingSpaceId)
  ).length;

  const submit = async () => {
    setSubmitting(true);
    setLoadError("");
    try {
      // Saved here rather than earlier so the operator can browse and choose
      // freely without a half-finished credential being written on the way in.
      const savedCredentialId = await ensureCredential();

      const payload =
        mode === PROVISION_MODES.IMPORT
          ? selectedImportRows.map((entry) => ({
              name: entry.name,
              calendarMeetingId: entry.id,
              locationId: importRows[entry.id].locationId,
              capacity:
                Number.parseInt(importRows[entry.id].capacity, 10) || undefined,
            }))
          : selectedMapRows.map((entry) => ({
              id: mapRows[entry.id].meetingSpaceId,
              calendarMeetingId: entry.id,
              name: entry.name,
            }));

      const result = await bulkCreateMeetingSpaces({
        mode,
        calendarCredentialId: savedCredentialId,
        defaultCapacity:
          Number.parseInt(defaultCapacity, 10) || DEFAULT_CAPACITY,
        meetingSpaces: payload,
        emmsId,
        emmsHeaders,
        requestConfig,
      });
      setSummary(result);
    } catch (e) {
      setLoadError(
        e?.response?.data ||
          e?.message ||
          "Meeting spaces could not be provisioned."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CheckCircleIcon color="success" />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Calendar entries
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
        <strong>{credentialName || "This calendar"}</strong>:{" "}
        <strong>{entries.length}</strong>{" "}
        {entries.length === 1 ? "entry" : "entries"} fetched.
      </Typography>
    </Box>
  );

  if (summary) {
    const notDone = (summary.results || []).filter(
      (result) => result.status === "skipped" || result.status === "failed"
    );
    const changed = (summary.createdCount || 0) + (summary.updatedCount || 0);
    return (
      <Box>
        {header}
        <Alert severity={notDone.length && !changed ? "warning" : "success"}>
          <AlertTitle sx={{ fontSize: "0.9rem" }}>
            {summary.mode === PROVISION_MODES.IMPORT
              ? `${summary.createdCount} meeting ${
                  summary.createdCount === 1 ? "room" : "rooms"
                } created`
              : `${summary.updatedCount} meeting ${
                  summary.updatedCount === 1 ? "room" : "rooms"
                } updated`}
          </AlertTitle>
          {notDone.length
            ? `${notDone.length} not processed.`
            : null}
        </Alert>

        {notDone.length > 0 && (
          <Box
            sx={{
              mt: 2,
              maxHeight: 280,
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Calendar entry</TableCell>
                  <TableCell width={220}>Calendar ID</TableCell>
                  <TableCell width={110}>Outcome</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notDone.map((result, index) => (
                  <TableRow key={`${result.calendarMeetingId}-${index}`}>
                    <TableCell>{result.name || "—"}</TableCell>
                    <TableCell>
                      <IdCell value={result.calendarMeetingId} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={result.status}
                        color={statusColour(result.status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {result.reason}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <Button
            variant="outlined"
            sx={{ textTransform: "none" }}
            onClick={backToChoices}
          >
            Do more
          </Button>
          <Button
            variant="contained"
            sx={{ textTransform: "none" }}
            onClick={onDone}
          >
            Done
          </Button>
        </Box>
      </Box>
    );
  }

  if (!mode) {
    return (
      <Box>
        {header}
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, display: "block", mb: 1 }}
        >
          Choose an option
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <ChoiceCard
            icon={<PlaylistAddIcon />}
            title="Import"
            caption="Create new meeting rooms from these entries."
            onClick={() => chooseMode(PROVISION_MODES.IMPORT)}
          />
          <ChoiceCard
            icon={<LinkIcon />}
            title="Map"
            caption="Link entries to existing meeting rooms."
            onClick={() => chooseMode(PROVISION_MODES.MAP)}
          />
          <ChoiceCard
            icon={<ScheduleIcon />}
            title="Do it later"
            caption="Save the credential only."
            onClick={doItLater}
            disabled={submitting}
          />
        </Box>

        <Box
          sx={{
            mt: 2,
            maxHeight: 280,
            overflowY: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Calendar entry</TableCell>
                <TableCell width={300}>Calendar ID</TableCell>
                <TableCell width={140}>Access</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {entry.name}
                    </Typography>
                    {entry.groupName ? (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {entry.groupName}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <IdCell value={entry.id} width={290} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={entry.canEdit === false ? "warning" : "default"}
                      label={entry.canEdit === false ? "read-only" : "writable"}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    );
  }

  const isImport = mode === PROVISION_MODES.IMPORT;

  return (
    <Box>
      {header}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: "none" }}
          onClick={backToChoices}
          disabled={submitting}
        >
          Back
        </Button>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {isImport
            ? "Import - create meeting rooms"
            : "Map - update existing meeting rooms"}
        </Typography>
        {!isImport && !loading && (
          <Chip
            size="small"
            variant="outlined"
            color={matchedCount ? "success" : "warning"}
            label={`${matchedCount} of ${entries.length} matched by name`}
          />
        )}
      </Box>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {typeof loadError === "string" ? loadError : "Something went wrong."}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          {isImport && (
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <TextField
                select
                size="small"
                label="Apply location to all"
                sx={{ minWidth: 280 }}
                value={bulkLocationId}
                onChange={(event) => applyLocationToAll(event.target.value)}
                disabled={locations.length === 0}
              >
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {locationLabel(location)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="number"
                label="Default capacity"
                sx={{ width: 170 }}
                inputProps={{ min: 1 }}
                value={defaultCapacity}
                onChange={(event) => setDefaultCapacity(event.target.value)}
              />
            </Box>
          )}

          <Box
            sx={{
              maxHeight: 380,
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Calendar entry</TableCell>
                  <TableCell width={200}>Calendar ID</TableCell>
                  {isImport ? (
                    <>
                      <TableCell width={320}>Location *</TableCell>
                      <TableCell width={140}>Capacity</TableCell>
                    </>
                  ) : (
                    <TableCell width={360}>Meeting room *</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => {
                  const row = isImport ? importRows[entry.id] : mapRows[entry.id];
                  const setRow = (patch) => {
                    const update = (previous) => ({
                      ...previous,
                      [entry.id]: { ...previous[entry.id], ...patch },
                    });
                    if (isImport) setImportRows(update);
                    else setMapRows(update);
                  };

                  return (
                    <TableRow key={entry.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={Boolean(row?.selected)}
                          onChange={(event) =>
                            setRow({ selected: event.target.checked })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {entry.name}
                        </Typography>
                        {entry.groupName ? (
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            {entry.groupName}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <IdCell value={entry.id} width={190} />
                      </TableCell>

                      {isImport ? (
                        <>
                          <TableCell>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              value={row?.locationId || ""}
                              onChange={(event) =>
                                setRow({ locationId: event.target.value })
                              }
                              error={Boolean(row?.selected) && !row?.locationId}
                              disabled={!row?.selected}
                            >
                              {locations.map((location) => (
                                <MenuItem key={location.id} value={location.id}>
                                  {locationLabel(location)}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              placeholder={String(defaultCapacity)}
                              inputProps={{ min: 1 }}
                              value={row?.capacity ?? ""}
                              onChange={(event) =>
                                setRow({ capacity: event.target.value })
                              }
                              disabled={!row?.selected}
                            />
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              value={row?.meetingSpaceId || ""}
                              onChange={(event) =>
                                setRow({
                                  meetingSpaceId: event.target.value,
                                  selected: true,
                                })
                              }
                              error={
                                Boolean(row?.selected) && !row?.meetingSpaceId
                              }
                            >
                              {mapOptionsFor(entry.id).map((meetingSpace) => (
                                <MenuItem
                                  key={meetingSpace.id}
                                  value={meetingSpace.id}
                                >
                                  {meetingSpace.name}
                                  {meetingSpace.meetingspacetype ? (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      sx={{ ml: 1, color: "text.secondary" }}
                                    >
                                      {meetingSpace.meetingspacetype === "emms"
                                        ? "EMMS"
                                        : "Calendar"}
                                    </Typography>
                                  ) : null}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              <EventAvailableIcon
                fontSize="inherit"
                sx={{ verticalAlign: "-2px", mr: 0.5 }}
              />
              {isImport
                ? `${selectedImportRows.length} of ${entries.length} selected to create`
                : `${selectedMapRows.length} of ${entries.length} selected to update`}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                color="error"
                sx={{ textTransform: "none" }}
                onClick={doItLater}
                disabled={submitting}
              >
                Do it later
              </Button>
              <Button
                variant="contained"
                sx={{ textTransform: "none" }}
                onClick={submit}
                disabled={submitting || (isImport ? !importReady : !mapReady)}
              >
                {submitting ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={18} color="inherit" />
                    {savingCredential ? "Saving credential…" : "Working…"}
                  </Box>
                ) : isImport ? (
                  "Create meeting rooms"
                ) : (
                  "Update meeting rooms"
                )}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CalendarProvisioningStep;
